import { LoadingOutlined } from "@ant-design/icons";
import type { IntlShape } from "@formatjs/intl";
import { Button, Form, Spin } from "antd";
import { useEffect, useMemo } from "react";
import { connect, type DispatchProp } from "react-redux";
import { actions } from "redux-router5";
import type { State as RouterState } from "router5";
import ConnectedAppLayout from "../../AppLayout.tsx";
import type { RootState } from "../../store.ts";
import ModalFooter from "../../../common/components/ModalFooter";
import { PageTitleContainer } from "../../../common/components/PageTitle.tsx";
import { routeNames } from "../../common/routes.ts";
import { clientActions } from "../../../features/client/clientSlice";
import type { Client } from "../../generated-axios-client/index.ts";
import { ClientFields } from "./ClientFields";

type CreateClientFormProps = {
	initialValue?: Client | null;
	intl: IntlShape;
	currentRoute: RouterState | null;
} & DispatchProp;

export function CreateClientForm(props: CreateClientFormProps) {
	const { dispatch, initialValue, intl, currentRoute } = props;

	const [form] = Form.useForm();

	const isEdit = !!initialValue?.id;

	const saveClient = () => {
		form
			.validateFields()
			.then((values) => {
				const editedValues = { ...values };
				if (initialValue?.id) {
					editedValues.id = initialValue.id;
				}
				dispatch(clientActions.addOrUpdateClient(editedValues));
			})
			.catch(() => {});
	};

	const initialValues = useMemo(() => {
		return initialValue ? { ...initialValue } : undefined;
	}, [initialValue]);

	useEffect(() => {
		if (initialValues) {
			form.setFieldsValue(initialValues);
		} else {
			form.resetFields();
		}
	}, [initialValues, form]);

	if (!currentRoute) {
		return <Spin indicator={<LoadingOutlined spin />} size="large" />;
	}

	return (
		<ConnectedAppLayout>
			<PageTitleContainer
				title={intl.formatMessage({
					id: isEdit ? "client_edit_title" : "client_create_title",
				})}
				subtitle={
					isEdit
						? undefined
						: intl.formatMessage({ id: "client_create_subtitle" })
				}
			>
				<Form form={form} layout="vertical" initialValues={initialValues}>
					<ClientFields intl={intl} />
				</Form>
				<ModalFooter>
					<Button
						onClick={() =>
							dispatch(
								actions.navigateTo(
									isEdit ? routeNames.CLIENT_VIEW : routeNames.CLIENT_LIST,
									isEdit ? { id: initialValue!.id } : undefined,
								),
							)
						}
					>
						{intl.formatMessage({ id: "cancel" })}
					</Button>
					<Button type="primary" onClick={saveClient}>
						{intl.formatMessage({ id: "confirm" })}
					</Button>
				</ModalFooter>
			</PageTitleContainer>
		</ConnectedAppLayout>
	);
}

export const CreateClientFormConnected = connect((state: RootState) => ({
	intl: state.intl.datetimeIntl,
	initialValue: state.client.client,
	currentRoute: state.router.route,
}))(CreateClientForm);

export default CreateClientFormConnected;
